import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { AirBookingFlightItinerarySlice } from "@b2bportal/air-booking-api";
import {
  fetchItinerary,
  FlightExchangePolicySelectors,
  FlightPostBookingSelectors,
} from "@b2bportal/core-flights";
import { type AppDispatch, CallState } from "@b2bportal/core-types";
import { navigateTo, TRIPS_PATH } from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import dayjs from "dayjs";

export type FlightExchangeConfirmationDataModel = {
  context: {
    itineraryDetails?: {
      locator?: string;
      slices: AirBookingFlightItinerarySlice[];
    };
    loading: boolean;
  };
  handlers: {
    goToMyTrips?: () => void;
  };
  text: {
    confirmationCodeLabel: string;
    pageTitle: string;
    pageBody?: string;
    status: string;
    viewTripDetailsCta: string;
  };
};

export const useFlightExchangeConfirmation = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useI18nContext();
  const originalItinerary = useSelector(
    FlightExchangePolicySelectors.getOriginalItinerary
  );
  const updatedItinerary = useSelector(
    FlightPostBookingSelectors.getSelectedFlightItinerary
  );

  useEffect(() => {
    if (originalItinerary) {
      const originalItineraryId = originalItinerary.bookedItinerary.id;

      dispatch(
        fetchItinerary({
          itineraryId: originalItineraryId,
          referenceDateTime: dayjs().format(),
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updatedItineraryDetails = useMemo(() => {
    if (updatedItinerary.data == null) return null;

    const {
      bookedItinerary: {
        id: updatedItineraryId,
        passengers: { contact },
        travelItinerary: { locators, slices },
      },
      emailAddress,
    } = updatedItinerary.data;
    const confirmationEmailAddress =
      emailAddress ?? contact.contact.emailAddress;

    return {
      context: {
        itineraryDetails: {
          slices,
          locator: locators?.b2b ?? locators?.agent.unscopedValue,
        },
      },
      handlers: {
        goToMyTrips: () =>
          navigateTo(`${TRIPS_PATH}/flights/${updatedItineraryId}`, {}),
      },
      text: {
        pageBody: t("exchangeable.core.confirmation.header.body", {
          context: "exchangeConfirmed",
          email: confirmationEmailAddress,
        }),
      },
    };
  }, [t, updatedItinerary.data]);

  return useMemo(() => {
    const dataModel: FlightExchangeConfirmationDataModel = {
      context: {
        ...updatedItineraryDetails?.context,
        loading: updatedItinerary.state === CallState.InProcess,
      },
      handlers: {
        ...updatedItineraryDetails?.handlers,
      },
      text: {
        ...updatedItineraryDetails?.text,
        confirmationCodeLabel: t(
          "exchangeable.core.confirmation.confirmationCodeLabel"
        ),
        pageTitle: t("exchangeable.core.confirmation.header.title", {
          context: "exchangeConfirmed",
        }),
        status: t("exchangeable.core.confirmation.status", {
          context: "exchangeConfirmed",
        }),
        viewTripDetailsCta: t(
          "exchangeable.core.confirmation.viewTripDetailsCta"
        ),
      },
    };

    return dataModel;
  }, [t, updatedItinerary.state, updatedItineraryDetails]);
};
